import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import ImageUpload from "@/components/ImageUpload";
import PatientTagsSelect from "@/components/patients/PatientTagsSelect";
import { PATIENTS } from "@/constants/testIds";

const PATIENT_PLACEHOLDER = "https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=entropy&cs=srgb&fm=jpg&q=85";

/** Pestana "Datos" de la ficha: contacto, médico, etiquetas y notas internas.
 * Recepcion solo puede editar/ver los datos de contacto (no lo médico). */
export default function PatientInfoTab({ patient, canEditMedical, onSave }) {
  const [form, setForm] = useState(patient);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(patient); }, [patient]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone || null,
        email: form.email || null,
        date_of_birth: form.date_of_birth || null,
        photo_url: form.photo_url || null,
        emergency_contact_name: form.emergency_contact_name || null,
        emergency_contact_phone: form.emergency_contact_phone || null,
      };
      if (canEditMedical) {
        payload.allergies = form.allergies || null;
        payload.medical_notes = form.medical_notes || null;
        payload.internal_notes = form.internal_notes || null;
        payload.tags = form.tags || [];
      }
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 max-w-3xl">
      <div className="flex items-center gap-4">
        <ImageUpload
          currentUrl={form.photo_url}
          onUploaded={(url) => setForm((f) => ({ ...f, photo_url: url }))}
          placeholderUrl={PATIENT_PLACEHOLDER}
          testId={PATIENTS.profilePhotoUpload}
        />
      </div>

      <section className="space-y-4">
        <h3 className="text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium">Datos personales y de contacto</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Nombre completo</Label>
            <Input value={form.name || ""} onChange={set("name")} data-testid={PATIENTS.infoNameInput} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Fecha de nacimiento</Label>
            <Input type="date" value={form.date_of_birth || ""} onChange={set("date_of_birth")} data-testid={PATIENTS.infoDobInput} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Teléfono</Label>
            <Input value={form.phone || ""} onChange={set("phone")} data-testid={PATIENTS.infoPhoneInput} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Email</Label>
            <Input type="email" value={form.email || ""} onChange={set("email")} data-testid={PATIENTS.infoEmailInput} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Contacto de emergencia</Label>
            <Input value={form.emergency_contact_name || ""} onChange={set("emergency_contact_name")} data-testid={PATIENTS.infoEmergencyNameInput} placeholder="Nombre" />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Teléfono de emergencia</Label>
            <Input value={form.emergency_contact_phone || ""} onChange={set("emergency_contact_phone")} data-testid={PATIENTS.infoEmergencyPhoneInput} />
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium">Información médica</h3>
        {!canEditMedical && (
          <p className="text-xs text-muted-foreground/80 italic" data-testid={PATIENTS.infoContactReadonlyNotice}>
            Recepcion no puede editar información médica. Solo lectura.
          </p>
        )}
        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Alergias</Label>
          <Textarea
            value={form.allergies || ""}
            onChange={set("allergies")}
            disabled={!canEditMedical}
            data-testid={PATIENTS.infoAllergiesInput}
            placeholder="Ej: Alergia a la penicilina"
            rows={2}
          />
        </div>
        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Observaciones médicas</Label>
          <Textarea
            value={form.medical_notes || ""}
            onChange={set("medical_notes")}
            disabled={!canEditMedical}
            data-testid={PATIENTS.infoMedicalNotesInput}
            placeholder="Condiciones preexistentes, medicacion actual, etc."
            rows={3}
          />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium">Etiquetas</h3>
        <PatientTagsSelect
          value={form.tags || []}
          onChange={(tags) => setForm((f) => ({ ...f, tags }))}
          disabled={!canEditMedical}
        />
      </section>

      <section className="space-y-3">
        <h3 className="text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium">Notas internas del equipo</h3>
        <Textarea
          value={form.internal_notes || ""}
          onChange={set("internal_notes")}
          disabled={!canEditMedical}
          data-testid={PATIENTS.infoInternalNotesInput}
          placeholder="Notas privadas visibles solo para el equipo (no para el paciente)"
          rows={3}
        />
      </section>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={PATIENTS.infoSaveButton}>
          <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar cambios"}
        </Button>
      </div>
    </div>
  );
}
